import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Dimensions,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { useIsFocused } from "@react-navigation/native";
import { FAB } from "react-native-paper";
import {
  MediumDeviceHeaders,
  headers,
  SmallDeviceHeaders,
} from "../components/headers/AdminOrganizationalUsers/headers";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { SalesOperationBadgeHorizontalList } from "../../../components/custom/Badge/SalesOperationBadgeHorizontalList";
import Spinner from "../../../components/shared/Spinner";
import { NestedDataTable } from "../../../components/custom/DataTable/NestedDataTableWithPagination2";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllOrganizationalUsers,
  searchOrganizationalUsers,
} from "../../../redux/dispatcher/adminDispatcher";
import { SearchAdminModulesWithSearchBar } from "../../../components/custom/Banners/SearchAdminModulesWithSearchBar";

export default function AdminOrganizationalUsers({ navigation }: any) {
  const isFocused = useIsFocused();
  const [isRefresh, setRefresh] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      headers
    );
  const { isLargeDevice, isMediumDevice, isMobileDevice, isSmallDevice } =
    useContext(DeviceOrientationContext);
  const dispatch = useDispatch<any>();
  const { organizationUsers, loadingOrganizationUsers } = useSelector(
    (state: any) => state.admin
  );
  const operationsFilterCategoryList = [
    "All",
    "On-Track",
    "At-Risk",
    "Delayed",
    "Completed",
  ];
  const fetchUsers = () => {
    dispatch(getAllOrganizationalUsers());
  };

  const listRef = useRef<ScrollView>(null);
  const [contentOffSet, setContentOffSet] = useState<number>(0);
  const [fabPosition] = useState<number>(320);
  const handleScroll = (event: any) => {
    setContentOffSet(event.nativeEvent.contentOffset.y);
  };

  const navigateToOrder = (userId: string, mode: string) => {
    navigation.navigate("AdminOrganizationalUsersNavigate", {
      mode: mode,
      userId: userId,
    });
  };

  useEffect(() => {
    setRefresh(false);
    setTableHeaders(
      isLargeDevice
        ? headers
        : isMediumDevice
        ? MediumDeviceHeaders
        : SmallDeviceHeaders
    );

    fetchUsers();
  }, [isFocused, isRefresh]);

  const renderFilters = () => (
    <View
      style={[styles.renderFilterStyles, !isLargeDevice && styles.mobileLayout]}
    >
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.filterContainer}
      >
        {!isSmallDevice && !isMobileDevice && (
          <Text style={styles.quickFilterText}>Quick Filter</Text>
        )}
        <SalesOperationBadgeHorizontalList
          itemsList={operationsFilterCategoryList}
          filterCategory={selectedCategory || ""}
          setFilterCategory={setSelectedCategory!}
          tableHeaders={tableHeaders}
        />
      </ScrollView>

      <SearchAdminModulesWithSearchBar
        titleName={`Total Items: ${organizationUsers.length}`}
        setRefresh={setRefresh}
        dataToExport={organizationUsers}
        fileName="userScreen"
        sheetName="Organizational Users Screen"
        disableTitle={true}
        noBorder={true}
        searchAPI={searchOrganizationalUsers}
      />
    </View>
  );

  return (
    <View style={styles.container}>
      <View style={styles.headerContainer}>
        <TouchableOpacity style={styles.addButton}>
          <Text
            style={styles.buttonText}
            onPress={() =>
              navigation.navigate("AdminHome", {
                screen: "AddAdminOrganizationUser",
              })
            }
          >
            Add User
          </Text>
        </TouchableOpacity>
      </View>
      <View style={styles.filterMainContainer}>{renderFilters()}</View>

      <ScrollView
        style={styles.contentContainer}
        onScroll={handleScroll}
        ref={listRef}
      >
        {loadingOrganizationUsers ? (
          <View style={styles.spinnerContainer}>
            <Spinner size="small" color="#74BBFF" />
          </View>
        ) : (
          <View style={{ backgroundColor: "#fff" }}>
            <FAB
              icon="arrow-up"
              style={[
                styles.fab,
                {
                  top:
                    contentOffSet +
                    Dimensions.get("window").height -
                    fabPosition,
                },
              ]}
              onPress={() =>
                listRef.current?.scrollTo({ x: 0, y: 0, animated: true })
              }
              small
            />
            <NestedDataTable
              itemsPerPage={15}
              isNestedDataLoaded={false}
              nestedHeader={tableHeaders}
              nestedRows={organizationUsers}
              key="rowKeyValue"
              userActions={navigateToOrder}
              userActionsKey="_id"
            />
          </View>
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
  },
  headerContainer: {
    flexDirection: "row",
    paddingVertical: 20,
    paddingHorizontal: 15,
    backgroundColor: "#fff",
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#000",
  },
  newOrderButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#1E88E5",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
  },
  newOrderText: {
    color: "#fff",
    marginRight: 5,
    fontWeight: "bold",
    cursor: "pointer",
  },
  filterMainContainer: {
    width: "100%",
  },
  filterContainer: {
    flexDirection: "row",
    paddingVertical: 10,
    paddingHorizontal: 15,
  },
  operationsContainer: {
    padding: 15,
    backgroundColor: "#fff",
  },
  operationCard: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#fff",
    padding: 15,
    borderRadius: 12,
    marginBottom: 10,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  operationCardContent: {
    flex: 1,
  },
  operationTitle: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 5,
  },
  operationStatus: {
    fontSize: 14,
    color: "#555",
  },
  contentContainer: {
    flex: 1,
    backgroundColor: "#fff",
  },
  spinnerContainer: {
    justifyContent: "center",
    alignItems: "center",
    marginTop: 20,
  },
  quickFilterText: {
    display: "flex",
    alignItems: "center",
    marginRight: 10,
  },
  renderFilterStyles: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 10,
  },
  mobileLayout: {
    flexDirection: "column",
  },
  fab: {
    position: "absolute",
    margin: 16,
    right: 0,
    zIndex: 1,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
  },
  buttonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "bold",
  },
  addButton: {
    backgroundColor: "#6495ED",
    paddingVertical: 10,
    paddingHorizontal: 15,
    borderRadius: 5,
  },
});
